// LocationSection.js
// Location section for the job dialog

import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Box,
  Button
} from '@mui/material';
import { MyLocation as MyLocationIcon } from '@mui/icons-material';
import 'leaflet/dist/leaflet.css'; // Import Leaflet CSS
import { locationTypes, usStates } from '../../constants/dashboardConstants';
import FilterSelect from '../common/FilterSelect';
// Import the reusable map component
import { JobMapComponent } from '../JobMapComponent'; // Corrected import path

/**
 * Location section component for the job dialog
 * @param {Object} props Component props
 * @param {Object} props.jobData Current job data
 * @param {Function} props.handleInputChange Function to handle input changes
 * @param {Function} props.handleNestedInputChange Function to handle nested input changes
 * @param {boolean} props.locationRequestSent Flag indicating if location request was sent
 * @param {boolean} props.locationLoading Flag indicating if location request is loading
 * @param {Function} props.handleRequestLocation Function to handle location request
 * @returns {JSX.Element} Location section component
 */
const COORD_PATTERN = /^-?\d*\.?\d*$/;

/** "32.9568, -97.3187" pasted into either box fills both. */
const splitPastedPair = (value) => {
  const match = String(value).trim().match(/^\(?\s*(-?\d{1,3}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*\)?$/);
  return match ? { lat: match[1], lng: match[2] } : null;
};

const CoordinateFields = ({ parent, location, handleNestedInputChange }) => {
  const update = (field) => (e) => {
    const value = e.target.value;
    const pair = splitPastedPair(value);
    if (pair) {
      handleNestedInputChange(parent, 'lat', pair.lat);
      handleNestedInputChange(parent, 'lng', pair.lng);
      return;
    }
    if (COORD_PATTERN.test(value)) handleNestedInputChange(parent, field, value);
  };
  const lat = location?.lat ?? '';
  const lng = location?.lng ?? '';
  const latBad = lat !== '' && (!Number.isFinite(Number(lat)) || Math.abs(Number(lat)) > 90);
  const lngBad = lng !== '' && (!Number.isFinite(Number(lng)) || Math.abs(Number(lng)) > 180);
  return (
    <>
      <Grid item xs={6} sm={4}>
        <TextField
          margin="dense"
          label="Latitude"
          fullWidth
          value={lat}
          onChange={update('lat')}
          error={latBad}
          helperText={latBad ? '-90 to 90' : 'Optional GPS pin'}
          inputProps={{ inputMode: 'decimal' }}
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <TextField
          margin="dense"
          label="Longitude"
          fullWidth
          value={lng}
          onChange={update('lng')}
          error={lngBad}
          helperText={lngBad ? '-180 to 180' : 'Paste "lat, lng" into either box'}
          inputProps={{ inputMode: 'decimal' }}
        />
      </Grid>
    </>
  );
};

const LocationSection = ({
  jobData,
  handleInputChange,
  handleNestedInputChange,
  locationRequestSent,
  locationLoading,
  handleRequestLocation,
 }) => {
  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Location</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<MyLocationIcon />}
          onClick={handleRequestLocation}
          disabled={!jobData.customerPhone || locationRequestSent || locationLoading}
        >
          {locationRequestSent ? 'LOCATION REQUESTED' : 'REQUEST LOCATION'}
        </Button>
      </Box>
      <Grid container spacing={2}>
        {/* Service Location */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>Service Location</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FilterSelect
                label="Location Type"
                required
                error={!jobData.serviceLocationType}
                value={jobData.serviceLocationType}
                options={locationTypes}
                onChange={(value) => handleInputChange('serviceLocationType', value)}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                margin="dense"
                label="Street Address *"
                fullWidth
                value={jobData.serviceLocation.street}
                onChange={(e) => handleNestedInputChange('serviceLocation', 'street', e.target.value)}
                required
                error={!jobData.serviceLocation.street}
                helperText={!jobData.serviceLocation.street ? "Required" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                margin="dense"
                label="City *"
                fullWidth
                value={jobData.serviceLocation.city}
                onChange={(e) => handleNestedInputChange('serviceLocation', 'city', e.target.value)}
                required
                error={!jobData.serviceLocation.city}
                helperText={!jobData.serviceLocation.city ? "Required" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FilterSelect
                label="State"
                required
                error={!jobData.serviceLocation.state}
                value={jobData.serviceLocation.state}
                options={usStates}
                onChange={(value) => handleNestedInputChange('serviceLocation', 'state', value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                margin="dense"
                label="ZIP Code *"
                fullWidth
                value={jobData.serviceLocation.zip}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[0-9]*$/.test(value)) { // Allow only numbers
                    handleNestedInputChange('serviceLocation', 'zip', value);
                  }
                }}
                required
                error={!jobData.serviceLocation.zip}
                helperText={!jobData.serviceLocation.zip ? "Required" : ""}
                inputProps={{ maxLength: 10 }} // Add maxLength to allow for ZIP+4
              />
            </Grid>
            <CoordinateFields parent="serviceLocation" location={jobData.serviceLocation} handleNestedInputChange={handleNestedInputChange} />
          </Grid>
        </Grid>

        {/* Dropoff Location */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Dropoff Location (Optional)</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FilterSelect
                label="Location Type"
                value={jobData.dropoffLocationType}
                options={locationTypes}
                onChange={(value) => handleInputChange('dropoffLocationType', value)}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                margin="dense"
                label="Street Address"
                fullWidth
                value={jobData.dropoffLocation.street}
                onChange={(e) => handleNestedInputChange('dropoffLocation', 'street', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                margin="dense"
                label="City"
                fullWidth
                value={jobData.dropoffLocation.city}
                onChange={(e) => handleNestedInputChange('dropoffLocation', 'city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FilterSelect
                label="State"
                value={jobData.dropoffLocation.state}
                options={usStates}
                onChange={(value) => handleNestedInputChange('dropoffLocation', 'state', value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                margin="dense"
                label="ZIP Code"
                fullWidth
                value={jobData.dropoffLocation.zip}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[0-9]*$/.test(value)) {
                    handleNestedInputChange('dropoffLocation', 'zip', value);
                  }
                }}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <CoordinateFields parent="dropoffLocation" location={jobData.dropoffLocation} handleNestedInputChange={handleNestedInputChange} />
          </Grid>
        </Grid>

        {/* Dropoff Map Display */}
        <Grid item xs={12}>
          <Box mt={2} style={{ height: '250px', border: '1px solid #ccc', position: 'relative' }}>
            <JobMapComponent locationData={jobData.dropoffLocation} />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default LocationSection;
